import { useEffect, useRef } from 'react'
import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from '../utils/gsap'

const DESKTOP_MQ = '(min-width: 1260px) and (pointer: fine)'

/**
 * Desktop smooth scroll with ScrollTrigger sync (Board ScrollSmoother equivalent).
 */
export function useLenis(enabled) {
  const lenisRef = useRef(null)
  const tickRef = useRef(null)

  useEffect(() => {
    if (!enabled) return undefined

    const mediaQuery = window.matchMedia(DESKTOP_MQ)

    const initLenis = () => {
      if (lenisRef.current) return

      const lenis = new Lenis({
        duration: 1.1,
        easing: (t) => Math.min(1, 1.001 - 2 ** (-10 * t)),
        smoothWheel: true,
        syncTouch: false,
        wheelMultiplier: 0.9,
      })

      lenisRef.current = lenis

      lenis.on('scroll', ScrollTrigger.update)

      ScrollTrigger.scrollerProxy(document.documentElement, {
        scrollTop(value) {
          if (arguments.length) {
            lenis.scrollTo(value, { immediate: true })
          }
          return lenis.scroll
        },
        getBoundingClientRect() {
          return {
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight,
          }
        },
        pinType: document.documentElement.style.transform ? 'transform' : 'fixed',
      })

      const tick = (time) => {
        lenis.raf(time * 1000)
      }
      tickRef.current = tick

      gsap.ticker.add(tick)
      gsap.ticker.lagSmoothing(0)

      const onRefresh = () => lenis.resize()
      ScrollTrigger.addEventListener('refresh', onRefresh)
      lenis._scrollPinRefresh = onRefresh

      requestAnimationFrame(() => ScrollTrigger.refresh())
    }

    const destroyLenis = () => {
      const lenis = lenisRef.current
      if (!lenis) return

      ScrollTrigger.scrollerProxy(document.documentElement, {})
      if (lenis._scrollPinRefresh) {
        ScrollTrigger.removeEventListener('refresh', lenis._scrollPinRefresh)
      }

      if (tickRef.current) {
        gsap.ticker.remove(tickRef.current)
        tickRef.current = null
      }

      lenis.destroy()
      lenisRef.current = null
    }

    const onMediaChange = () => {
      destroyLenis()
      if (mediaQuery.matches) initLenis()
    }

    if (mediaQuery.matches) initLenis()
    mediaQuery.addEventListener('change', onMediaChange)

    return () => {
      mediaQuery.removeEventListener('change', onMediaChange)
      destroyLenis()
    }
  }, [enabled])
}
